import React, { ChangeEvent, useEffect } from 'react';
import { useMemo } from 'react';
import { InfluencerAdditionalItemsTable } from './InfluencerAdditionalItemsTable';
import { InfluencerOfferPlatformTable } from './InfluencerOfferPlatformTable';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { InfluencerOptionalItemsTable } from './InfluencerOptionalItemsTable';
import _debounce from 'lodash/debounce';
import { useDispatch, useSelector } from 'react-redux';
import {
    setPersonalOfferDescription,
    setInviteAttachment,
} from '../../../../../../../store/influencer/offerInfluencerSlice';
import { IAllStates } from '../../../../../../../store/rootReducer';
import { InfluencerOfferHeader } from './InfluencerOfferHeader';
import { DeclineOffer } from './DeclineOffer';
import { Totals } from '../../../../../../brand/campaign/book/offer/modal/tabs/offer/Totals';
import {
    calcFee,
    downloadFile,
    formatDate,
    getSubtotalAdditionalItems,
    getSubtotalOfferItems,
} from '../../../../../../../shared/functions/Functions';
import { TotalTypes } from '../../../../../../../enum/TotalTypes';
import { InfluencerFeeTotals } from '../../../../../../../shared/components/offer/InfluencerFeeTotals';
import ItemValueSection from '../../../../../../../shared/components/offer/ItemValueSection';
import { UploadImage } from '../../../../../../../shared/components/UploadImage';
import FilePlaceholder from '../../../../../../../shared/components/placeholder/FilePlaceholder';
import { Fancybox } from '../../../../../../../shared/components/Fancybox';
import { Fancybox as NativeFancybox } from '@fancyapps/ui';
import TooltipOverlay from '../../../../../../../shared/components/tooltip/Tooltip';
import { getTruncatedData } from '../../../../../../../utils/helpers/getTruncatedData';
import { PackageIncludedInfo } from '../../../../../../../shared/components/offer/PackageIncludedInfo';

export function InfluencerOfferTab() {
    const { t } = useTranslation();
    const {
        initialOffer,
        isEditable,
        campaign,
        offerModalStatus,
        isCharity,
        feePercentage,
        hasFee,
        isIvConnectedClub,
        inviteAttachment,
    } = useSelector((state: IAllStates) => state.offerInfluencer);
    const dispatch = useDispatch();

    const additionalItemsSum = useMemo(
        () => getSubtotalAdditionalItems(initialOffer.distribution),
        [initialOffer.distribution]
    );

    const contractItemsSum = useMemo(() => getSubtotalOfferItems(initialOffer.offerItems), [initialOffer.offerItems]);
    const legalDocument = campaign?.offers[0]?.legalDocuments?.[0];
    const { truncatedName: truncatedFileName, isTruncated: isFileNameTruncated } = getTruncatedData(
        legalDocument?.originalName ?? '',
        30
    );
    const { truncatedName: truncatedUploaderName, isTruncated: isUploaderNameTruncated } = getTruncatedData(
        legalDocument?.uploadedBy?.displayName ?? '',
        20
    );
    const legalDocumentSecond = campaign?.contract?.legalDocuments?.[0];
    const { truncatedName: truncatedFileNameSecond, isTruncated: isFileNameTruncatedSecond } = getTruncatedData(
        legalDocumentSecond?.originalName ?? '',
        30
    );
    const { truncatedName: truncatedUploaderNameSecond, isTruncated: isUploaderNameTruncatedSecond } = getTruncatedData(
        legalDocumentSecond?.uploadedBy?.displayName ?? '',
        20
    );
    const displayFiles =
        inviteAttachment !== undefined
            ? inviteAttachment
            : offerModalStatus === 'negotiate' && legalDocument
            ? [
                  {
                      name: legalDocument.originalName ?? legalDocument.name,
                      type: legalDocument.mime,
                  } as unknown as Blob,
              ]
            : undefined;

    useEffect(() => {
        if (offerModalStatus === 'negotiate' && legalDocument && inviteAttachment === undefined) {
            dispatch(
                setInviteAttachment([
                    {
                        name: legalDocument.originalName ?? legalDocument.name,
                        type: legalDocument.mime,
                    } as unknown as Blob,
                ])
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offerModalStatus, legalDocument?.id]);

    const { serviceFeeSum, grandTotalSum, subTotalAndAddons } = useMemo(() => {
        const baseTotal = additionalItemsSum + contractItemsSum;
        const fee = hasFee ? calcFee(baseTotal, feePercentage) : 0;
        return {
            serviceFeeSum: Math.round(fee),
            grandTotalSum: baseTotal - Math.round(fee),
            subTotalAndAddons: baseTotal,
        };
    }, [additionalItemsSum, contractItemsSum, hasFee, feePercentage, isIvConnectedClub]);

    const changeHandler = _debounce((event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = event?.target?.value;
        dispatch(setPersonalOfferDescription(value));
    }, 300);

    const saveInviteFile: React.Dispatch<React.SetStateAction<Blob[] | MediaSource[] | undefined>> = value => {
        const resolvedFiles = typeof value === 'function' ? value(inviteAttachment) : value;
        const normalizedFiles = Array.isArray(resolvedFiles)
            ? resolvedFiles
            : resolvedFiles
            ? [resolvedFiles]
            : undefined;
        dispatch(setInviteAttachment(normalizedFiles as Blob[] | MediaSource[] | undefined));
    };

    return (
        <>
            <DeclineOffer
                declineComment={campaign?.offers[0]?.declineComment}
                declineReason={campaign?.offers[0]?.declineReason}
                status={campaign?.offers[0]?.status}
                cancellationNote={campaign?.offers[0]?.cancellationNote}
                cancellationDocumentName={campaign?.offers[0]?.cancellationDocumentName}
                cancellationDocumentPath={campaign?.offers[0]?.cancellationDocumentPath}
                cancellationDocumentUploadedAt={campaign?.offers[0]?.cancellationDocumentUploadedAt}
            />
            <InfluencerOfferHeader
                createdAt={campaign?.offers[0]?.createdAt ?? ''}
                offerModalStatus={offerModalStatus}
                isEditable={isEditable}
                isRequested={campaign?.offers[0]?.isRequested}
                status={campaign?.offers[0]?.status}
                acceptedDate={campaign?.offers[0]?.acceptedDate ?? ''}
            />
            <PackageIncludedInfo isPackageIncluded={campaign?.offers[0]?.isPackageIncluded} />

            <div>
                {!campaign?.offers[0]?.isAddonsOnly ? (
                    <InfluencerOfferPlatformTable />
                ) : (
                    <div className="d-flex align-items-center mb-2">
                        <h6 className="ml-md-2 ml-1 mb-0">{t(`influencer.offer_management.modal.addons_only`)}</h6>
                        <p className="mb-0 ml-1">
                            - {t(`influencer.offer_management.modal.add-ons-only-desc`)}
                            <span className="ml-1 font-weight-bold">({campaign?.parent?.title})</span>
                        </p>
                    </div>
                )}
                {!campaign?.offers[0]?.isLongTerm && (
                    <>
                        <InfluencerAdditionalItemsTable />
                        <div className="w-100 my-2">
                            <Totals
                                price={additionalItemsSum}
                                isCharity={isCharity}
                                title={TotalTypes.ADDITIONAL_ITEMS}
                            />
                        </div>
                    </>
                )}
                {(!isCharity || (isCharity && grandTotalSum > 0)) && (
                    <InfluencerFeeTotals
                        subTotalAndAddons={subTotalAndAddons}
                        serviceFeeSum={serviceFeeSum}
                        grandTotalSum={grandTotalSum}
                        feePercentage={feePercentage}
                        hasFee={hasFee}
                        isIvConnectedClub={isIvConnectedClub}
                        isSpendingOffer={campaign?.offers[0]?.isLongTerm || campaign?.offers[0]?.isAddonsOnly}
                    />
                )}
                {offerModalStatus === 'negotiate' &&
                    campaign?.offerPlaceholder?.distribution &&
                    campaign?.offerPlaceholder?.distribution?.length > 0 &&
                    !isCharity && <InfluencerOptionalItemsTable />}

                {isCharity && !!campaign?.charityValueDesc && (
                    <ItemValueSection classes="px-1" description={campaign?.charityValueDesc} />
                )}

                {offerModalStatus === 'negotiate' && (
                    <>
                        <h6 className="mt-3">{t('influencer.offer_management.modal.add_personal_offer')}</h6>
                        <div className="row">
                            <div className="col-md-6">
                                <Form.Control
                                    id="basic-textarea"
                                    onChange={changeHandler}
                                    defaultValue={initialOffer.personalOffer}
                                    as="textarea"
                                    name="personalOffer"
                                    rows={5}
                                    disabled={campaign?.isCharity}
                                    placeholder={t('influencer.offer_management.modal.add_personal_offer_placeholder')}
                                />
                            </div>
                            <div className="col-md-6 mt-3 mt-md-0">
                                <div className="offer-dropzone">
                                    <UploadImage
                                        saveFile={saveInviteFile}
                                        isMultiple={false}
                                        initialFiles={displayFiles}
                                        isFullHeight={true}
                                        acceptedFormats="application/pdf"
                                        centerText={true}
                                        fileName={true}
                                    />
                                </div>
                            </div>
                        </div>
                    </>
                )}
                {offerModalStatus !== 'negotiate' && campaign?.offers[0]?.personalOffer && (
                    <div className="mt-4 px-2">
                        <div>
                            <h6>{t('influencer.offer_management.modal.add_personal_offer')}</h6>
                        </div>
                        <p className="text-muted mb-0">{campaign?.offers[0]?.personalOffer}</p>
                    </div>
                )}
                {offerModalStatus !== 'negotiate' && campaign?.offers[0]?.legalDocuments?.[0] && (
                    <div className="mt-3 px-1">
                        <div
                            className="d-flex align-items-start p-2 border rounded position-relative"
                            style={{ maxWidth: 300 }}
                        >
                            <Fancybox>
                                <FilePlaceholder
                                    file={campaign?.offers[0]?.legalDocuments?.[0]}
                                    hideText={true}
                                    classIcon="font-22"
                                    styleContent={{ width: 40, height: 40, flexShrink: 0 }}
                                    nameFancybox="legal-document"
                                    handleDownload={() =>
                                        downloadFile(
                                            campaign?.offers[0]?.legalDocuments?.[0]?.path ?? '',
                                            campaign?.offers[0]?.legalDocuments?.[0]?.originalName
                                        )
                                    }
                                    right={-10}
                                />
                            </Fancybox>
                            <div className="ms-2">
                                <TooltipOverlay content={isFileNameTruncated ? legalDocument?.originalName : ''}>
                                    <p
                                        className="mb-0 fw-semibold cursor-pointer"
                                        onClick={() =>
                                            NativeFancybox.show([
                                                {
                                                    src: campaign?.offers[0]?.legalDocuments?.[0]?.path,
                                                    type: 'iframe',
                                                },
                                            ])
                                        }
                                    >
                                        {truncatedFileName || '-'}
                                    </p>
                                </TooltipOverlay>
                                <p className="mb-0 text-muted fs-12">
                                    {t('influencer.offer_management.modal.uploaded_by')}:{' '}
                                    <TooltipOverlay
                                        content={isUploaderNameTruncated ? legalDocument?.uploadedBy?.displayName : ''}
                                        dFlex={false}
                                    >
                                        <span>{truncatedUploaderName || '-'}</span>
                                    </TooltipOverlay>
                                </p>
                                <p className="mb-0 text-muted fs-12">
                                    {t('influencer.offer_management.modal.uploaded_at')}:{' '}
                                    {formatDate(campaign?.offers[0]?.legalDocuments?.[0]?.updatedAt)}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
                {campaign?.contract?.personalOffer && (
                    <div className="mt-4 px-2">
                        <div>
                            <h6>{t('influencer.offer_management.modal.add_personal_offer')}</h6>
                        </div>
                        <p className="text-muted mb-0">{campaign?.contract?.personalOffer}</p>
                    </div>
                )}
                {campaign?.contract?.legalDocuments?.[0] && (
                    <div className="mt-3 px-1">
                        <div
                            className="d-flex align-items-start p-2 border rounded position-relative"
                            style={{ maxWidth: 300 }}
                        >
                            <Fancybox>
                                <FilePlaceholder
                                    file={campaign?.contract?.legalDocuments?.[0]}
                                    hideText={true}
                                    classIcon="font-22"
                                    styleContent={{ width: 40, height: 40, flexShrink: 0 }}
                                    nameFancybox="legal-document"
                                    handleDownload={() =>
                                        downloadFile(
                                            campaign?.contract?.legalDocuments?.[0]?.path ?? '',
                                            campaign?.contract?.legalDocuments?.[0]?.originalName
                                        )
                                    }
                                    right={-10}
                                />
                            </Fancybox>
                            <div className="ms-2">
                                <TooltipOverlay
                                    content={isFileNameTruncatedSecond ? legalDocumentSecond?.originalName : ''}
                                >
                                    <p
                                        className="mb-0 fw-semibold cursor-pointer"
                                        onClick={() =>
                                            NativeFancybox.show([
                                                {
                                                    src: campaign?.contract?.legalDocuments?.[0]?.path,
                                                    type: 'iframe',
                                                },
                                            ])
                                        }
                                    >
                                        {truncatedFileNameSecond || '-'}
                                    </p>
                                </TooltipOverlay>
                                <p className="mb-0 text-muted fs-12">
                                    {t('influencer.offer_management.modal.uploaded_by')}:{' '}
                                    <TooltipOverlay
                                        content={
                                            isUploaderNameTruncatedSecond
                                                ? legalDocumentSecond?.uploadedBy?.displayName
                                                : ''
                                        }
                                        dFlex={false}
                                    >
                                        <span>{truncatedUploaderNameSecond || '-'}</span>
                                    </TooltipOverlay>
                                </p>
                                <p className="mb-0 text-muted fs-12">
                                    {t('influencer.offer_management.modal.uploaded_at')}:{' '}
                                    {formatDate(campaign?.contract?.legalDocuments?.[0]?.updatedAt)}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
